import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import {
    EditButtons,
    PackagingFormatInput,
    PourTypeInput,
    ProductCategoryInput,
    ServingContainerInput,
} from '@barboxx/admin-components';
import { POUR_FORMAT_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { PourFormat } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    pourFormat: PourFormat.DetailsResponse;
}

export const PourFormatDetailsCard = ({ pourFormat }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const formContext = useForm<PourFormat.UpdateRequest>({
        resolver: yupResolver(POUR_FORMAT_SCHEMA),
        defaultValues: pourFormat,
    });

    const mutateUser = useMutation({
        mutationKey: ['pourFormat', pourFormat.id],
        mutationFn: (data: PourFormat.UpdateRequest) =>
            Api.Servings.PourFormat.putPourFormat(pourFormat.id, data),
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: PourFormat.UpdateRequest) => {
        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated pour format!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error updating pour format!', {
                    severity: 'error',
                });
            });
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Pour Format Details" />
                <CardContent>
                    <Stack gap={2}>
                        <PourTypeInput disabled={!editable} />
                        <ProductCategoryInput disabled={!editable} />
                        <ServingContainerInput disabled={!editable} />
                        <PackagingFormatInput disabled={!editable} />
                        <TextFieldElement
                            fullWidth
                            name="volumeMl"
                            disabled={!editable}
                            label="Volume (ml)"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 10000,
                                    step: 1,
                                },
                            }}
                        />
                        <TextFieldElement
                            fullWidth
                            name="volumeOz"
                            disabled={!editable}
                            label="Volume (oz)"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 1000,
                                    step: 0.1,
                                },
                            }}
                        />
                        <TextFieldElement
                            fullWidth
                            name="displayOrder"
                            disabled={!editable}
                            label="Display Order"
                            type="number"
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    max: 1000,
                                    step: 1,
                                },
                            }}
                        />
                        <CheckboxElement
                            label="Is Default"
                            name="isDefault"
                            disabled={!editable}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="PourFormat">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
